<template>
  <div class="vud">
    <!-- Eligibility banner -->
    <div class="vud-banner">
      <div class="vud-banner-badge">
        <v-icon color="white" size="20">mdi-shield-check</v-icon>
      </div>
      <div>
        <div class="vud-banner-title">Qualified For Assessment</div>
        <div class="vud-banner-sub">
          This policy meets the requirements and is eligible for assessment.
        </div>
      </div>
    </div>

    <v-row>
      <v-col cols="12" md="6">
        <div class="vud-card">
          <div class="vud-card-head">
            <div class="vud-head-icon">
              <v-icon color="primary" size="18">mdi-account-outline</v-icon>
            </div>
            <span class="vud-card-title">Insured &amp; Policy Information</span>
          </div>
          <div v-for="(row, i) in insuredInfo" :key="i" class="vud-row">
            <div class="vud-row-icon">
              <v-icon color="primary" size="16">{{ row.icon }}</v-icon>
            </div>
            <span class="vud-label">{{ row.label }}</span>
            <span class="vud-value">{{ row.value }}</span>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="vud-card">
          <div class="vud-card-head">
            <div class="vud-head-icon">
              <v-icon color="primary" size="18"
                >mdi-file-document-outline</v-icon
              >
            </div>
            <span class="vud-card-title">Policy Details</span>
          </div>
          <div v-for="(row, i) in policyDetails" :key="i" class="vud-row">
            <div class="vud-row-icon">
              <v-icon color="primary" size="16">{{ row.icon }}</v-icon>
            </div>
            <span class="vud-label">{{ row.label }}</span>
            <span class="vud-value">{{ row.value }}</span>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/**
 * Presentational "Vehicle & User Details" body used inside several dialogs
 * (booked-vehicles view, vehicle-inspection view, view-more-details). Expects a
 * booking-shaped `item` with `clientInfo`, `vehicleInfo`, and `policyInfo`.
 * Read-only: emits nothing and holds no state.
 */
export default {
  name: 'VehicleUserDetails',
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    insuredInfo() {
      const d = this.item || {}
      return [
        {
          icon: 'mdi-account-multiple-outline',
          label: "Insured's Name",
          value: d.clientInfo?.name,
        },
        {
          icon: 'mdi-phone-outline',
          label: "Insured's Phone Number",
          value: d.clientInfo?.phoneNumber,
        },
        {
          icon: 'mdi-card-account-details-outline',
          label: 'Reg No',
          value: d.vehicleInfo?.registrationNumber,
        },
        {
          icon: 'mdi-file-document-outline',
          label: 'Policy No',
          value: d.policyInfo?.policyNumber,
        },
        {
          icon: 'mdi-shield-check-outline',
          label: 'Sum Assured',
          value: d.policyInfo?.sumInsured,
        },
        {
          icon: 'mdi-seal-variant',
          label: 'Endorsement Number',
          value: d.policyInfo?.endorsementNumber,
        },
      ]
    },
    policyDetails() {
      const d = this.item || {}
      return [
        {
          icon: 'mdi-calendar-outline',
          label: 'Policy Type',
          value: d.policyInfo?.coverType,
        },
        {
          icon: 'mdi-calendar-text-outline',
          label: 'Policy Description',
          value: d.policyInfo?.description,
        },
        {
          icon: 'mdi-calendar-start',
          label: 'Policy Period from',
          value: d.policyInfo?.effectiveFromDate,
        },
        {
          icon: 'mdi-calendar-end',
          label: 'Policy Period to',
          value: d.policyInfo?.effectiveToDate,
        },
        {
          icon: 'mdi-calendar-refresh-outline',
          label: 'Renewal Date',
          value: d.policyInfo?.renewalDate,
        },
      ]
    },
  },
}
</script>

<style scoped>
.vud-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #eef4fd;
  border: 1px solid #dce8fb;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.vud-banner-badge {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #034ea2 0%, #0a63c4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}
.vud-banner-title {
  color: #034ea2;
  font-weight: 800;
  font-size: 15px;
  line-height: 1.3;
}
.vud-banner-sub {
  color: #5b6b82;
  font-size: 13px;
}
.vud-card {
  border: 1px solid #e8edf5;
  border-radius: 14px;
  padding: 14px 16px;
  height: 100%;
}
.vud-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.vud-head-icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #eaf1fb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vud-card-title {
  color: #0f2a55;
  font-weight: 800;
  font-size: 15px;
}
.vud-row {
  display: grid;
  grid-template-columns: 26px minmax(96px, auto) 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 2px;
  border-bottom: 1px solid #eef2f8;
}
.vud-row:last-child {
  border-bottom: none;
}
.vud-row-icon {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: #f2f6fc;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vud-label {
  color: #6b7a90;
  font-size: 13px;
}
.vud-value {
  color: #034ea2;
  font-weight: 700;
  font-size: 13px;
  text-align: right;
  overflow-wrap: break-word;
}
</style>
